import React, { useState, useEffect } from 'react';
import Header from './Header';
import Footer from './Footer';
import LoanTypeTab from './ui/LoanTypeTab';
import LoanTypeContent from './ui/LoanTypeContent';
import { Home, Car, Briefcase, Wallet } from 'lucide-react';
import i18n from '../i18n/config';
import CTASection from './ui/CTASection';

interface LoanType {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  color: string;
  period: string;
  rate: string;
  security: string;
  benefits: string[];
  details: string[];
  faq: Array<{
    question: string;
    answer: string;
  }>;
}

const LoanTypesPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('mortgage');
  const [currentLanguage, setCurrentLanguage] = useState(i18n.locale || 'lv');

  // Handle URL hash changes
  useEffect(() => {
    // Update current language
    setCurrentLanguage(i18n.locale || document.documentElement.lang || 'lv');
    
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      // Map anchor links to loan type IDs
      const anchorToId: { [key: string]: string } = {
        [i18n.t('anchor_links.mortage')]: 'mortgage',
        [i18n.t('anchor_links.business')]: 'business',
        [i18n.t('anchor_links.car')]: 'car',
        [i18n.t('anchor_links.consumer')]: 'consumer'
      };
      
      if (hash && anchorToId[hash]) {
        setActiveTab(anchorToId[hash]);
      } else if (hash && ['mortgage', 'business', 'car', 'consumer'].includes(hash)) {
        setActiveTab(hash);
      }
    };

    // Set initial tab from URL hash
    handleHashChange();
    
    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);
    
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  // Update URL hash when tab changes
  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    // Map loan type IDs to anchor links
    const idToAnchor: { [key: string]: string } = {
      'mortgage': i18n.t('anchor_links.mortage'),
      'business': i18n.t('anchor_links.business'),
      'car': i18n.t('anchor_links.car'),
      'consumer': i18n.t('anchor_links.consumer')
    };
    
    const anchor = idToAnchor[tabId] || tabId;
    window.history.pushState(null, '', `#${anchor}`);
  };

  const loanTypes: LoanType[] = [
    {
      id: 'mortgage',
      title: i18n.t('loan_types_page.mortgage.title'),
      description: i18n.t('loan_types_page.mortgage.description'),
      icon: Home,
      color: '#1abc9c',
      period: i18n.t('loan_types_page.mortgage.period'),
      rate: i18n.t('loan_types_page.mortgage.rate'),
      security: i18n.t('loan_types_page.mortgage.security'),
      benefits: [
        i18n.t('loan_types_page.mortgage.benefits.convenient'),
        i18n.t('loan_types_page.mortgage.benefits.fast'),
        i18n.t('loan_types_page.mortgage.benefits.quality'),
        i18n.t('loan_types_page.mortgage.benefits.accessible'),
        i18n.t('loan_types_page.mortgage.benefits.profitable'),
        i18n.t('loan_types_page.mortgage.benefits.pause')
      ],
      details: [
        i18n.t('loan_types_page.mortgage.details.suitable'),
        i18n.t('loan_types_page.mortgage.details.evaluation'),
        i18n.t('loan_types_page.mortgage.details.processing')
      ],
      faq: [
        {
          question: i18n.t('loan_types_page.mortgage.faq.early_repayment.question'),
          answer: i18n.t('loan_types_page.mortgage.faq.early_repayment.answer')
        },
        {
          question: i18n.t('loan_types_page.mortgage.faq.purpose_check.question'),
          answer: i18n.t('loan_types_page.mortgage.faq.purpose_check.answer')
        },
        {
          question: i18n.t('loan_types_page.mortgage.faq.partial_funding.question'),
          answer: i18n.t('loan_types_page.mortgage.faq.partial_funding.answer')
        }
      ]
    },
    {
      id: 'business',
      title: i18n.t('loan_types_page.business.title'),
      description: i18n.t('loan_types_page.business.description'),
      icon: Briefcase,
      color: '#3498db',
      period: i18n.t('loan_types_page.business.period'),
      rate: i18n.t('loan_types_page.business.rate'),
      security: i18n.t('loan_types_page.business.security'),
      benefits: [
        i18n.t('loan_types_page.business.benefits.convenient'),
        i18n.t('loan_types_page.business.benefits.fast'),
        i18n.t('loan_types_page.business.benefits.quality'),
        i18n.t('loan_types_page.business.benefits.accessible'),
        i18n.t('loan_types_page.business.benefits.profitable'),
        i18n.t('loan_types_page.business.benefits.pause')
      ],
      details: [
        i18n.t('loan_types_page.business.details.suitable'),
        i18n.t('loan_types_page.business.details.security')
      ],
      faq: [
        {
          question: i18n.t('loan_types_page.business.faq.early_repayment.question'),
          answer: i18n.t('loan_types_page.business.faq.early_repayment.answer')
        },
        {
          question: i18n.t('loan_types_page.business.faq.purpose_check.question'),
          answer: i18n.t('loan_types_page.business.faq.purpose_check.answer')
        },
        {
          question: i18n.t('loan_types_page.business.faq.partial_funding.question'),
          answer: i18n.t('loan_types_page.business.faq.partial_funding.answer')
        }
      ]
    },
    {
      id: 'car',
      title: i18n.t('loan_types_page.car.title'),
      description: i18n.t('loan_types_page.car.description'),
      icon: Car,
      color: '#f1c40f',
      period: i18n.t('loan_types_page.car.period'),
      rate: i18n.t('loan_types_page.car.rate'),
      security: i18n.t('loan_types_page.car.security'),
      benefits: [
        i18n.t('loan_types_page.car.benefits.convenient'),
        i18n.t('loan_types_page.car.benefits.accessible')
      ],
      details: [
        i18n.t('loan_types_page.car.details.full_service'),
        i18n.t('loan_types_page.car.details.partners')
      ],
      faq: [
        {
          question: i18n.t('loan_types_page.car.faq.buyout.question'),
          answer: i18n.t('loan_types_page.car.faq.buyout.answer')
        },
        {
          question: i18n.t('loan_types_page.car.faq.down_payment.question'),
          answer: i18n.t('loan_types_page.car.faq.down_payment.answer')
        },
        {
          question: i18n.t('loan_types_page.car.faq.extension.question'),
          answer: i18n.t('loan_types_page.car.faq.extension.answer')
        }
      ]
    },
    {
      id: 'consumer',
      title: i18n.t('loan_types_page.consumer.title'),
      description: i18n.t('loan_types_page.consumer.description'),
      icon: Wallet,
      color: '#e74c3c',
      period: i18n.t('loan_types_page.consumer.period'),
      rate: i18n.t('loan_types_page.consumer.rate'),
      security: i18n.t('loan_types_page.consumer.security'),
      benefits: [
        i18n.t('loan_types_page.consumer.benefits.convenient'),
        i18n.t('loan_types_page.consumer.benefits.fast'),
        i18n.t('loan_types_page.consumer.benefits.quality'),
        i18n.t('loan_types_page.consumer.benefits.accessible'),
        i18n.t('loan_types_page.consumer.benefits.profitable'),
        i18n.t('loan_types_page.consumer.benefits.pause')
      ],
      details: [
        i18n.t('loan_types_page.consumer.details.suitable'),
        i18n.t('loan_types_page.consumer.details.security')
      ],
      faq: [
        {
          question: i18n.t('loan_types_page.consumer.faq.early_repayment.question'),
          answer: i18n.t('loan_types_page.consumer.faq.early_repayment.answer')
        },
        {
          question: i18n.t('loan_types_page.consumer.faq.purpose_check.question'),
          answer: i18n.t('loan_types_page.consumer.faq.purpose_check.answer')
        },
        {
          question: i18n.t('loan_types_page.consumer.faq.partial_funding.question'),
          answer: i18n.t('loan_types_page.consumer.faq.partial_funding.answer')
        }
      ]
    }
  ];

  const activeLoanType = loanTypes.find(type => type.id === activeTab) || loanTypes[0];

  return (
    <div className="min-h-screen flex flex-col bg-[#f9fafb]">
      <Header 
        currentLanguage={currentLanguage}
      />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-[#2c3e50] to-[#3498db] text-white py-16 overflow-hidden">
          <div className="absolute inset-0 bg-[url('/pattern.png')] opacity-20"></div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center max-w-4xl mx-auto">
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight mb-6">
                {i18n.t('loan_types_page.hero.title')}
              </h1>
              <p className="text-xl text-white/90 leading-relaxed mb-8">
                {i18n.t('loan_types_page.hero.description')}
              </p>
            </div>
          </div>
        </section>

        {/* Loan Types Tabs */}
        <section className="py-8 bg-white border-b border-gray-200">
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap justify-center gap-4">
              {loanTypes.map((loanType) => (
                <LoanTypeTab
                  key={loanType.id}
                  id={loanType.id}
                  title={loanType.title}
                  icon={loanType.icon}
                  color={loanType.color}
                  isActive={activeTab === loanType.id}
                  onClick={handleTabChange}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Loan Type Content */}
        <section className="py-16 bg-gradient-to-br from-white to-[#f9fafb]">
          <div className="container mx-auto px-4">
            <LoanTypeContent loanType={activeLoanType} />
          </div>
        </section>

        {/* Call to Action */}
        <CTASection />
      </main>
      
      <Footer />
    </div>
  );
};

export default LoanTypesPage; 